import {
  ENFORCED_PLAN_FEATURES,
  PLAN_FEATURE_LABELS_FA,
  aiImportPagesOf,
  effectivePlanFeatures,
  offerDiscountRials,
  offerEligibility,
  planFeatureForLinkReason,
  revenueShareBp,
  termDiscountBp,
  termSavingRials,
  type EnforcedPlanFeature,
  type FlatPlanPrice,
  type PlanLinkModel,
  type PlanLinkReason,
  type PlanLinkTerm,
  type RaasRate,
} from '@bookora/shared';

import type {
  ClinicOfferResponse,
  PlanResponse,
  PlanTermResponse,
  ResultRateResponse,
  SubscriptionResponse,
} from '@/api/generated/model';

/**
 * The plan catalogue, read off `GET /billing/plans` — pure, so the screen and
 * its tests decide «which plans, which terms, which model» the same way.
 *
 * Nothing here knows a price: every figure is a plan row's (or a term's), and
 * the derived ones (discount, saving) go through the shared pricing module.
 */

/** FLAT plans in catalogue order (`sortOrder`, then price). */
export function flatPlansOf(plans: readonly PlanResponse[]): PlanResponse[] {
  return plans
    .filter((p) => p.billingModel !== 'RAAS' && p.isActive !== false)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.priceRials - b.priceRials);
}

/**
 * The «نتیجه‌محور» plan, if one is active AND has a rate in force. The plan is
 * created in production through the console, never by the seed, so its
 * absence is normal — the model switch is then not drawn at all.
 */
export function raasPlanOf(plans: readonly PlanResponse[]): PlanResponse | null {
  return (
    plans.find(
      (p) => p.billingModel === 'RAAS' && p.isActive !== false && p.resultRate?.current != null,
    ) ?? null
  );
}

/** The cheapest flat plan by its ONE-MONTH price — «مطب» on the launch catalogue. */
export function cheapestFlatPlan(plans: readonly PlanResponse[]): PlanResponse | null {
  const flat = flatPlansOf(plans);
  if (flat.length === 0) return null;
  return flat.reduce((a, b) => (b.priceRials < a.priceRials ? b : a));
}

/** The shared formula's view of a rate row. */
export function toRaasRate(rate: ResultRateResponse): RaasRate {
  return {
    platformFeeRials: rate.platformFeeRials,
    includedQty: rate.includedQty,
    unitPriceRials: rate.unitPriceRials,
    capRials: rate.capRials,
  };
}

/** What `comparePlansForVolume` compares against: each flat plan's one-month price. */
export function flatPricesOf(plans: readonly PlanResponse[]): FlatPlanPrice[] {
  return flatPlansOf(plans).map((p) => ({ key: p.key, name: p.name, monthlyRials: p.priceRials }));
}

/** The terms any flat plan offers, ascending (1 is always among them when a plan exists). */
export function offeredTerms(plans: readonly PlanResponse[]): number[] {
  const months = new Set<number>();
  for (const plan of flatPlansOf(plans)) for (const term of plan.terms) months.add(term.months);
  return [...months].sort((a, b) => a - b);
}

export function termOf(plan: PlanResponse, months: number): PlanTermResponse | null {
  return plan.terms.find((t) => t.months === months) ?? null;
}

/**
 * A term's discount against paying month by month, in basis points, as the
 * catalogue states it on the term control: the featured plan's when it offers
 * the term, else the first plan's that does. `null` when no plan offers it or
 * there is no discount.
 */
export function termDiscountOf(plans: readonly PlanResponse[], months: number): number | null {
  const flat = flatPlansOf(plans);
  const ordered = [...flat.filter((p) => p.isFeatured), ...flat.filter((p) => !p.isFeatured)];
  for (const plan of ordered) {
    const term = termOf(plan, months);
    if (!term || months <= 1) continue;
    const bp = termDiscountBp(plan.priceRials, term.priceRials, months);
    return bp > 0 ? bp : null;
  }
  return null;
}

/** What a term saves on this plan against `months` × its monthly price (0 when none). */
export function termSavingOf(plan: PlanResponse, term: PlanTermResponse): number {
  if (term.months <= 1) return 0;
  return Math.max(0, termSavingRials(plan.priceRials, term.priceRials, term.months));
}

/**
 * The model the catalogue opens on: the link's `?model=`, then the current
 * subscription's, then flat — and always flat when no RaaS plan exists.
 */
export function initialModel(
  linkModel: PlanLinkModel | null,
  currentModel: 'FLAT' | 'RAAS' | null | undefined,
  raasAvailable: boolean,
): PlanLinkModel {
  if (!raasAvailable) return 'flat';
  if (linkModel) return linkModel;
  return currentModel === 'RAAS' ? 'raas' : 'flat';
}

/**
 * The term the flat view opens on: the link's `?term=` when some plan offers
 * it, else MONTHLY (owner round 4: the default period is monthly everywhere).
 */
export function initialTerm(linkTerm: PlanLinkTerm | null, offered: readonly number[]): number {
  if (linkTerm && offered.includes(linkTerm)) return linkTerm;
  return 1;
}

/**
 * Whether `plan` includes an enforced feature, as the server decides it:
 * `effectivePlanFeatures` (so `branches` is derived from the location limit,
 * never read from the stored flags), and photo import only with pages.
 */
export function planIncludes(plan: PlanResponse, feature: EnforcedPlanFeature): boolean {
  if (feature === 'aiImport') return aiImportPagesOf(plan) !== 0;
  const effective = effectivePlanFeatures({
    features: plan.features,
    maxLocations: plan.limits.maxLocations,
  });
  return effective[feature] === true;
}

/**
 * The tag on the card a link points at, by why it was pointed at: one per
 * enforced feature («پیشنهاد برای {label}»), the photo import in its short
 * name, and a plain «پلن پیشنهادی» for a link without a reason.
 */
export function suggestionTag(reason: PlanLinkReason | null): string {
  if (reason === null) return 'پلن پیشنهادی';
  if (reason === 'ai-import') return 'پیشنهاد برای ثبت اطلاعات از روی عکس';
  return `پیشنهاد برای ${PLAN_FEATURE_LABELS_FA[planFeatureForLinkReason(reason)]}`;
}

/**
 * The ROI line's share, or `null` when there is nothing sensible to say.
 *
 * `revenueShareBp` guards its inputs by THROWING (a non-safe integer, or a
 * revenue past 2^53) — right for the meter, wrong in a render: an owner who
 * holds a key down in «میانگین مبلغ هر خدمت» must not take the whole
 * subscription tab into the error boundary. An amount the formula refuses is
 * simply not a figure the line can state.
 */
export function safeRevenueShareBp(
  billRials: number,
  treated: number,
  averageRials: number | null,
): number | null {
  if (averageRials === null || !Number.isSafeInteger(averageRials) || averageRials <= 0) {
    return null;
  }
  try {
    return revenueShareBp(billRials, treated, averageRials);
  } catch {
    return null;
  }
}

/**
 * What a sales offer does to one plan card on one term — display only (the
 * quote re-checks everything). `null` when the offer does not apply or takes
 * nothing off the price; RaaS never carries a price offer.
 */
export function offerFor(
  plan: PlanResponse,
  termMonths: number,
  offer: ClinicOfferResponse | null,
): { listRials: number; offerRials: number } | null {
  if (!offer || plan.billingModel === 'RAAS') return null;
  const term = termOf(plan, termMonths);
  if (!term) return null;
  const terms = {
    percentOffBp: offer.percentOffBp,
    capRials: offer.capRials,
    amountOffRials: offer.amountOffRials,
    freeMonths: offer.freeMonths,
    trialDays: offer.trialDays,
    smsCreditRials: offer.smsCreditRials,
    duration: offer.duration,
    cycles: offer.cycles,
    planKeys: offer.planKeys,
    termMonths: offer.termMonths,
    firstPaidOnly: false,
  };
  const verdict = offerEligibility(
    {
      grantId: offer.grantId,
      offerId: offer.grantId,
      viaCode: false,
      active: true,
      capReached: false,
      expiresAt: offer.expiresAt ? new Date(offer.expiresAt) : null,
      terms,
    },
    {
      planKey: plan.key,
      billingModel: 'FLAT',
      termMonths,
      listPriceRials: term.priceRials,
      monthlyRials: plan.priceRials,
      hasPaidBefore: false,
      negotiated: false,
      now: new Date(),
    },
  );
  const discount = verdict.ok ? offerDiscountRials(term.priceRials, terms) : 0;
  return discount > 0 ? { listRials: term.priceRials, offerRials: term.priceRials - discount } : null;
}

/** The monthly rate of a term price, rounded like the server's `monthlyRecurringRials`. */
function monthlyRateRials(priceRials: number, months: number): number {
  return months > 0 ? Math.round(Math.max(0, Math.trunc(priceRials)) / months) : 0;
}

/**
 * Whether choosing this FLAT plan on this term would be SCHEDULED for the end
 * of the current period rather than applied now — the server's
 * `decideChangeTiming` for a clinic caller (which never asks for a timing):
 *
 *  - nothing live, or a row that no longer grants writes → now;
 *  - flat → flat: a DOWNGRADE by monthly rate (`isPlanDowngrade`, against the
 *    row's own snapshot price and term) waits for the period end;
 *  - RaaS → flat: now on a trial or when the target unlocks an enforced
 *    feature, else at the period end.
 *
 * A scheduled change is billed by the sweep without an offer, so the card must
 * not show an offer price for it (the quote's `offer` is null there too).
 */
export function choiceIsScheduled(
  live: Pick<
    SubscriptionResponse,
    'status' | 'writesAllowed' | 'billingModel' | 'priceRials' | 'termMonths' | 'plan'
  > | null,
  plan: PlanResponse,
  termMonths: number,
): boolean {
  if (!live || !live.writesAllowed) return false;
  const target = termOf(plan, termMonths);
  if (!target) return false;
  if (live.billingModel === 'RAAS') {
    if (live.status === 'TRIALING') return false;
    const gains = ENFORCED_PLAN_FEATURES.some(
      (f) => planIncludes(plan, f) && !planIncludes(live.plan, f),
    );
    return !gains;
  }
  return (
    monthlyRateRials(target.priceRials, termMonths) <
    monthlyRateRials(live.priceRials, live.termMonths ?? 1)
  );
}
